import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import {
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import DeleteIcon from "@mui/icons-material/Delete";

const INSERTED_TEXT_FONT_OPTIONS = [
  { value: "Helvetica", label: "Helvetica / Arial" },
  { value: "Times-Roman", label: "Times Roman" },
  { value: "Courier", label: "Courier" },
  { value: "Dancing Script", label: "Dancing Script" },
  { value: "Patrick Hand", label: "Patrick Hand" },
  { value: "Caveat", label: "Caveat" },
  { value: "Gloria Hallelujah", label: "Gloria Hallelujah" },
];

// Used only to preview each font in the dropdown.
const FONT_PREVIEW_CSS = {
  Helvetica: "Helvetica, Arial, sans-serif",
  "Times-Roman": "'Times New Roman', Times, serif",
  Courier: "'Courier New', Courier, monospace",
  "Dancing Script": "'Dancing Script', cursive",
  "Patrick Hand": "'Patrick Hand', cursive",
  Caveat: "'Caveat', cursive",
  "Gloria Hallelujah": "'Gloria Hallelujah', cursive",
};

const MIN_FONT_SIZE = 1;

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: "text.secondary",
    }}
  >
    {children}
  </Typography>
);

const InsertedTextInput = ({
  edit,
  documentKey,
  onFieldChange,
  disabled,
  active,
}) => {
  const [draft, setDraft] = useState(edit?.new_text ?? "");
  const inputRef = useRef(null);
  const sessionRef = useRef(null);
  const latestRef = useRef(null);
  const timerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(maxWaitTimerRef.current);
    timerRef.current = null;
    maxWaitTimerRef.current = null;
  }, []);

  useLayoutEffect(() => {
    latestRef.current = { edit, documentKey, onFieldChange, disabled, active };
  });

  useLayoutEffect(() => {
    const text = edit?.new_text ?? "";
    const session = sessionRef.current;
    if (
      !session ||
      session.documentKey !== documentKey ||
      session.editId !== edit?.edit_id ||
      session.targetId !== edit?.target_item_id ||
      session.page !== edit?.page ||
      session.committedText !== text ||
      disabled ||
      !active
    ) {
      // Undo/redo, external edits and selection/document changes win over drafts.
      clearTimers();
      sessionRef.current = {
        documentKey,
        editId: edit?.edit_id,
        targetId: edit?.target_item_id,
        page: edit?.page,
        committedText: text,
        draftText: text,
      };
      setDraft(text);
    }
  }, [
    edit?.edit_id,
    edit?.target_item_id,
    edit?.page,
    edit?.new_text,
    documentKey,
    disabled,
    active,
    clearTimers,
  ]);

  useLayoutEffect(() => clearTimers, [clearTimers]);

  const commitDraft = useCallback(() => {
    clearTimers();
    const latest = latestRef.current;
    const session = sessionRef.current;
    if (
      !session ||
      !latest?.active ||
      latest.disabled ||
      typeof latest.onFieldChange !== "function" ||
      latest.documentKey !== session.documentKey ||
      latest.edit?.edit_id !== session.editId ||
      latest.edit?.target_item_id !== session.targetId ||
      latest.edit?.page !== session.page ||
      latest.edit?.kind !== "inserted" ||
      latest.edit?.isEditorAdded !== true ||
      latest.edit?.action === "delete" ||
      (latest.edit?.new_text ?? "") !== session.committedText ||
      session.draftText === session.committedText
    ) {
      return;
    }

    session.committedText = session.draftText;
    // Use the newest guarded callback, not the one from the first keystroke.
    latest.onFieldChange("text", session.draftText);
  }, [clearTimers]);

  useLayoutEffect(() => {
    if (!active || disabled) return undefined;

    const flushPendingDraft = () => {
      const session = sessionRef.current;
      if (!session || session.draftText === session.committedText) return;
      // Publish before Save, formatting, deletion or a new selection reads state.
      flushSync(commitDraft);
    };
    const flushBeforeOutsideInteraction = (event) => {
      if (event.target !== inputRef.current) flushPendingDraft();
    };
    const flushBeforeShortcut = (event) => {
      if (
        event.target !== inputRef.current ||
        event.key === "Tab" ||
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s")
      ) {
        flushPendingDraft();
      }
    };

    window.addEventListener("pointerdown", flushBeforeOutsideInteraction, true);
    window.addEventListener("click", flushBeforeOutsideInteraction, true);
    window.addEventListener("focusin", flushBeforeOutsideInteraction, true);
    window.addEventListener("keydown", flushBeforeShortcut, true);
    window.addEventListener("blur", flushPendingDraft);
    return () => {
      window.removeEventListener(
        "pointerdown",
        flushBeforeOutsideInteraction,
        true,
      );
      window.removeEventListener("click", flushBeforeOutsideInteraction, true);
      window.removeEventListener(
        "focusin",
        flushBeforeOutsideInteraction,
        true,
      );
      window.removeEventListener("keydown", flushBeforeShortcut, true);
      window.removeEventListener("blur", flushPendingDraft);
    };
  }, [active, disabled, commitDraft]);

  return (
    <TextField
      hiddenLabel
      fullWidth
      size="small"
      multiline
      minRows={2}
      maxRows={6}
      placeholder="Type the text to add…"
      disabled={disabled}
      inputRef={inputRef}
      value={draft}
      onBlur={commitDraft}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
      onChange={(event) => {
        const session = sessionRef.current;
        if (!session || disabled || !active) return;
        const text = event.target.value;
        session.draftText = text;
        setDraft(text);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(commitDraft, 250);
        if (maxWaitTimerRef.current === null) {
          maxWaitTimerRef.current = setTimeout(commitDraft, 1000);
        }
      }}
    />
  );
};

const InsertedTextSettings = ({
  edit,
  documentKey,
  onFieldChange,
  disabled,
  active,
}) => {
  const settings = edit?.textSettings || {};
  const fontFamily = settings.fontFamily || "Helvetica";
  const fontSize =
    Number.isFinite(Number(settings.fontSize)) && Number(settings.fontSize) > 0
      ? Number(settings.fontSize)
      : 12;
  const [fontSizeDraft, setFontSizeDraft] = useState(String(fontSize));

  useEffect(() => {
    setFontSizeDraft(String(fontSize));
  }, [fontSize]);

  const isValidFontSize =
    fontSizeDraft.trim() !== "" &&
    Number.isFinite(Number(fontSizeDraft)) &&
    Number(fontSizeDraft) > 0;

  const commitFontSize = () => {
    if (disabled || !isValidFontSize) {
      setFontSizeDraft(String(fontSize));
      return;
    }

    const nextFontSize = Number(fontSizeDraft);
    setFontSizeDraft(String(nextFontSize));
    if (nextFontSize !== fontSize) {
      onFieldChange?.("textSettings", { fontSize: nextFontSize });
    }
  };

  const stepFontSize = (delta) => {
    if (disabled) return;
    const next = Math.max(
      MIN_FONT_SIZE,
      Math.round((fontSize + delta) * 2) / 2,
    );
    setFontSizeDraft(String(next));
    if (next !== fontSize) {
      onFieldChange?.("textSettings", { fontSize: next });
    }
  };

  const activeFormats = [
    settings.bold === true && "bold",
    settings.italic === true && "italic",
  ].filter(Boolean);

  const handleFormatChange = (_event, nextFormats) => {
    if (disabled || !Array.isArray(nextFormats)) return;
    onFieldChange?.("textSettings", {
      bold: nextFormats.includes("bold"),
      italic: nextFormats.includes("italic"),
    });
  };

  const stepButtonSx = {
    border: 1,
    borderColor: "divider",
    borderRadius: 1.5,
    width: 36,
    height: 36,
    color: "text.secondary",
    "&:hover": { bgcolor: "action.hover", color: "primary.main" },
  };

  return (
    <Stack
      spacing={2}
      onKeyDown={(event) => {
        // Capture flushes pending text before the existing Save shortcut runs.
        if (
          (event.ctrlKey || event.metaKey) &&
          event.key.toLowerCase() === "s"
        ) {
          return;
        }
        event.stopPropagation();
      }}
    >
      {/* Content */}
      <Stack spacing={0.75}>
        <SectionLabel>Text</SectionLabel>
        <InsertedTextInput
          edit={edit}
          documentKey={documentKey}
          onFieldChange={onFieldChange}
          disabled={disabled}
          active={active}
        />
      </Stack>

      {/* Font */}
      <Stack spacing={0.75}>
        <SectionLabel>Font</SectionLabel>
        <TextField
          hiddenLabel
          fullWidth
          size="small"
          select
          disabled={disabled}
          value={fontFamily}
          onChange={(event) =>
            onFieldChange?.("textSettings", { fontFamily: event.target.value })
          }
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            "& .MuiSelect-select": {
              fontFamily: FONT_PREVIEW_CSS[fontFamily],
            },
          }}
        >
          {!INSERTED_TEXT_FONT_OPTIONS.some(
            (option) => option.value === fontFamily,
          ) && <MenuItem value={fontFamily}>{fontFamily}</MenuItem>}
          {INSERTED_TEXT_FONT_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ fontFamily: FONT_PREVIEW_CSS[option.value] }}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <IconButton
            size="small"
            aria-label="Decrease font size"
            disabled={disabled || fontSize <= MIN_FONT_SIZE}
            onClick={() => stepFontSize(-1)}
            sx={stepButtonSx}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <TextField
            hiddenLabel
            size="small"
            type="number"
            disabled={disabled}
            value={fontSizeDraft}
            error={!isValidFontSize}
            helperText={!isValidFontSize ? "Must be greater than 0" : undefined}
            inputProps={{
              min: 0.1,
              step: 0.5,
              "aria-label": "Font size in points",
              style: { textAlign: "center" },
            }}
            InputProps={{
              endAdornment: (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", ml: 0.5 }}
                >
                  pt
                </Typography>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
              "& input[type=number]": { MozAppearance: "textfield" },
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                { WebkitAppearance: "none", margin: 0 },
            }}
            onChange={(event) => setFontSizeDraft(event.target.value)}
            onBlur={commitFontSize}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.querySelector("input")?.blur();
              } else if (event.key === "Escape") {
                event.preventDefault();
                setFontSizeDraft(String(fontSize));
              }
            }}
          />
          <IconButton
            size="small"
            aria-label="Increase font size"
            disabled={disabled}
            onClick={() => stepFontSize(1)}
            sx={stepButtonSx}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Style */}
      <Stack spacing={0.75}>
        <SectionLabel>Style</SectionLabel>
        <ToggleButtonGroup
          fullWidth
          size="small"
          disabled={disabled}
          value={activeFormats}
          onChange={handleFormatChange}
          aria-label="Text style"
          sx={{
            gap: 2,

            "& .MuiToggleButtonGroup-grouped": {
              margin: 0,
              border: "1px solid",
              borderColor: "divider",
              "&:not(:first-of-type)": {
                borderRadius: 1.5,
                borderLeft: "1px solid",
                borderColor: "divider",
              },
              "&:not(:last-of-type)": {
                borderRight: "1px solid",
                borderColor: "divider",
              },
            },
            "& .MuiToggleButton-root": {
              gap: 1.5,
              textTransform: "none",
              fontWeight: 500,
              color: "text.secondary",
              borderRadius: 1.5,
              "&:hover": { bgcolor: "action.hover" },
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderColor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
              },
            },
          }}
        >
          <ToggleButton value="bold" aria-label="Bold">
            <FormatBoldIcon fontSize="small" />
            Bold
          </ToggleButton>
          <ToggleButton value="italic" aria-label="Italic">
            <FormatItalicIcon fontSize="small" />
            Italic
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Divider />

      <Button
        fullWidth
        size="small"
        color="error"
        variant="outlined"
        startIcon={<DeleteIcon />}
        disabled={disabled}
        onClick={() => onFieldChange?.("action", "delete")}
        sx={{ textTransform: "none", borderRadius: 1.5 }}
      >
        Delete added text
      </Button>
    </Stack>
  );
};

export default InsertedTextSettings;
